import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';

const FAQSection = ({ currentLanguage }) => {
  const [expandedItems, setExpandedItems] = useState([]);

  const content = {
    EN: {
      faqs: [
        {
          category: "Getting Started",
          questions: [
            {
              question: "Do I need a bank account?",
              answer: "Yes you need an active bank account"
            },
            {
              question: "What do I need to apply?",
              answer: "To apply, you need to be at least 18 years old, a Canadian resident, have a valid government-issued ID, a verifiable address, a phone number, and the ability to load funds onto your card. No credit check is required for approval."
            },
            {
              question: "How quickly can I get my card?",
              answer: "Once approved, your PSPXpress card will be delivered within 7-10 business days. You can start using it immediately after activation, which can be done online or by phone."
            }
          ]
        },
        {
          category: "Credit Building",
          questions: [
            {
              question: "How is my credit information shared with Equifax?",
              answer: "PSPXpress may report your account activity to Equifax monthly. This includes your payment history, account balance, and usage patterns. However, reporting is not guaranteed and depends on various factors including your account standing and activity level."
            },
            {
              question: "Does this guarantee my score will increase?",
              answer: "No. Responsible use may help show positive credit activity, but results vary. Credit scores depend on multiple factors including your overall credit history, existing debts, and other financial behaviors. PSPXpress cannot guarantee credit score improvement."
            },
            {
              question: "How long before I see results?",
              answer: "If your activity is reported to Equifax, you may see changes in your credit report within 1-2 months. However, meaningful credit score improvements typically take 3-6 months of consistent, responsible usage. Results vary by individual."
            }
          ]
        },
        {
          category: "Card Usage",
          questions: [
            {
              question: "Where can I use my card?",
              answer: "Your PSPXpress Mastercard can be used anywhere Mastercard is accepted worldwide - online, in-store, for bill payments, and at ATMs. This includes restaurants, retail stores, online shopping, and subscription services."
            },
            {
              question: "Are there any fees?",
              answer: "PSPXpress has a transparent fee structure. There may be fees for ATM withdrawals, monthly maintenance (waived with minimum activity), and certain transactions. All fees are clearly disclosed before you apply."
            },
            {
              question: "Can I reload my card?",
              answer: "Yes! You can reload your card through direct deposit, cash deposits at participating retailers, online bank transfers, or by visiting authorized reload locations. Funds are typically available within minutes for most reload methods."
            }
          ]
        }
      ]
    },
    FR: {
      faqs: [
        {
          category: "Pour commencer",
          questions: [
            {
              question: "Ai-je besoin d\'un compte bancaire?",
              answer: "Oui, vous avez besoin d'un compte bancaire actif"
            },
            {
              question: "De quoi ai-je besoin pour postuler?",
              answer: "Pour postuler, vous devez avoir au moins 18 ans, être résident canadien, avoir une pièce d'identité valide émise par le gouvernement, une adresse vérifiable, un numéro de téléphone et la capacité de charger des fonds sur votre carte. Aucune vérification de crédit n'est requise pour l'approbation."
            },
            {
              question: "À quelle vitesse puis-je obtenir ma carte?",
              answer: "Une fois approuvée, votre carte PSPXpress sera livrée dans les 7-10 jours ouvrables. Vous pouvez commencer à l'utiliser immédiatement après activation, qui peut être faite en ligne ou par téléphone."
            }
          ]
        },
        {
          category: "Construction de crédit",
          questions: [
            {
              question: "Comment mes informations de crédit sont-elles partagées avec Equifax?",
              answer: "PSPXpress peut signaler l'activité de votre compte à Equifax mensuellement. Cela inclut votre historique de paiement, le solde du compte et les modèles d'utilisation. Cependant, le signalement n'est pas garanti et dépend de divers facteurs, y compris votre statut de compte et le niveau d'activité."
            },
            {
              question: "Cela garantit-il que mon score augmentera?",
              answer: "Non. L'utilisation responsable peut aider à montrer une activité de crédit positive, mais les résultats varient. Les scores de crédit dépendent de multiples facteurs, y compris votre historique de crédit global, les dettes existantes et autres comportements financiers. PSPXpress ne peut garantir l'amélioration du score de crédit."
            },
            {
              question: "Combien de temps avant de voir des résultats?",
              answer: "Si votre activité est signalée à Equifax, vous pourriez voir des changements dans votre rapport de crédit dans les 1-2 mois. Cependant, des améliorations significatives du score de crédit prennent typiquement 3-6 mois d'utilisation cohérente et responsable. Les résultats varient selon l'individu."
            }
          ]
        },
        {
          category: "Utilisation de la carte",
          questions: [
            {
              question: "Où puis-je utiliser ma carte?",
              answer: "Votre Mastercard PSPXpress peut être utilisée partout où Mastercard est acceptée dans le monde - en ligne, en magasin, pour le paiement de factures et aux guichets automatiques. Cela inclut les restaurants, les magasins de détail, les achats en ligne et les services d'abonnement."
            },
            {
              question: "Y a-t-il des frais?",
              answer: "PSPXpress a une structure de frais transparente. Il peut y avoir des frais pour les retraits aux guichets automatiques, l'entretien mensuel (annulé avec une activité minimum), et certaines transactions. Tous les frais sont clairement divulgués avant de postuler."
            },
            {
              question: "Puis-je recharger ma carte?",
              answer: "Oui! Vous pouvez recharger votre carte par dépôt direct, dépôts en espèces chez les détaillants participants, virements bancaires en ligne, ou en visitant les endroits de rechargement autorisés. Les fonds sont généralement disponibles en quelques minutes pour la plupart des méthodes de rechargement."
            }
          ]
        }
      ]
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  const toggleExpanded = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setExpandedItems(prev => 
      prev?.includes(key) 
        ? prev?.filter(item => item !== key)
        : [...prev, key]
    );
  };

  const isExpanded = (categoryIndex, questionIndex) => {
    return expandedItems?.includes(`${categoryIndex}-${questionIndex}`);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 max-w-4xl">
        {text?.faqs?.map((category, categoryIndex) => (
          <div key={categoryIndex} className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 border-b-2 border-primary pb-2">
              {category?.category}
            </h2>
            
            <div className="space-y-4">
              {category?.questions?.map((faq, questionIndex) => (
                <motion.div
                  key={questionIndex}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: questionIndex * 0.1 }}
                  viewport={{ once: true }}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleExpanded(categoryIndex, questionIndex)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-gray-900 pr-4">
                      {faq?.question}
                    </span>
                    <motion.div
                      animate={{ rotate: isExpanded(categoryIndex, questionIndex) ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Icon 
                        name="ChevronDown" 
                        size={20} 
                        className="text-gray-500 flex-shrink-0" 
                      />
                    </motion.div>
                  </button>
                  
                  <AnimatePresence>
                    {isExpanded(categoryIndex, questionIndex) && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                          <p className="text-gray-600 leading-relaxed">
                            {faq?.answer}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FAQSection;